import { useEffect, useState, useRef } from 'react';
import { useConversation } from '@11labs/react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Avatar, Box } from '@mui/material';
import { createClient, LiveTranscriptionEvents } from "@deepgram/sdk";
import { sessionExpireNotices } from "../helper/Const";
import { connectRealtime } from "../../../managers/realtimeManager";
import { handleServerEvent } from "../../../managers/handleServerEvents";

import "./speech-to-text.css";

// Constants
const sessionResumeText = "Interview paused. Please click the button to resume when you are ready.";
let globalChatHistory = [];
var promptJson;
var systemPrompt;
var initialQuestion;
var interviewQuestion;
var promptId;
const chatCompletionHist = [];
var dg_mic;
var dg_socket;
const dgFinalTranscriptHist = [];
let interviewStartTime;

// For non-agent approach
var timeOutSeconds = 2500;
var timeOutSecondsAfterJoining = 1000;
var sessionTimeOutSeconds = 30000;
var sessionAliveRetryTimes = 2;
var remainingRetryTimes = 2;
var isPostingNotice = false;
let waitingTimeout;
var aiTimeOutSeconds = 500;
let uuid;


const { v4: uuidv4 } = require('uuid');



const SpeechToText = ({
    isMicOn, 
    isccOn, 
    resume, 
    jd, 
    candidate, 
    round, 
    language,
    setIntvSpeaking, 
    setCandSpeaking, 
    setInterviewPrompt,
    quickMockJobId,
    agentId,
    isB2BInterview,
    jobListingId,
    isPracticeMode,
    practiceQuestion
}) => {
    // State variables
    const [chatHistory, setChatHistory] = useState([]);
    const [jobId, setJobId] = useState(null);
    const [QMJobId, setQMJobId] = useState(null);
    const [defaultInterview, setDefaultInterview] = useState(false);
    const [hasInitialQuestionFinished, setInitialQuestionFinished] = useState(false);
    const [isUttering, setIsUttering] = useState(false);
    const [isSocketOn, setSocketOn] = useState(false);
    const [isMicrophoneOn, setMicrophoneOn] = useState(false);
    const isMicrophoneOnRef = useRef(isMicrophoneOn);
    const [dgFinalTranscript, setDgFinalTranscript] = useState("");
    const [dgTempTranscript, setDgTempTranscript] = useState("");
    
    // Realtime API states
    const [realtimeTranscriptions, setRealtimeTranscriptions] = useState([]);
    const [realtimeResponses, setRealtimeResponses] = useState([]);
    const isRealtimeConnectedRef = useRef(false);
    const peerConnectionRef = useRef(null);
    const realtimeDataChannelRef = useRef(null);
    const localStreamRef = useRef(null);
    
    const audioElementRef = useRef(null);
    const wsRef = useRef(null);
    const sessionExpireNotice = sessionExpireNotices[language] || "Sorry I can't hear you. Could you repeat what you said?";
    const messagesEndRef = useRef(null);

    const updatePromptRef = useRef(() => {});
    const usingAgent = !!agentId;
    const usingRealtime = !agentId;
    const [messageItems, setMessageItems] = useState([]);


    useEffect(() => {
    updatePromptRef.current = (role, content) => {
        promptJson = {
        conversation: [
            ...promptJson.conversation,
            {
            role: role,
            content: content,
            },
        ],
        };
    };
    }, []);

    // Initialize ElevenLabs conversation hook
    const conversation = useConversation({
        onConnect: () => {
            console.log("[11labs] Connected to websocket");
            setSocketOn(true);
        },
        onDisconnect: () => {
            console.log("[11labs] Disconnected from websocket");
            setSocketOn(false);
            setIntvSpeaking(false)
            setCandSpeaking(false);
        },
        onMessage: (message) => {
            handleMessage(message);
        },
        onError: (error) => {
            console.error("[11labs] Error:", error);
            showReconnectionPopUp();
        }
    });

    const { status, isSpeaking } = conversation;
    
    

    const handleMessage = (message) => {
        console.log("message is:", message);
        if (message.source === 'ai') {
            updateChatHistory(message.message, 'assistant');
        } else if (message.source === 'user') {
            updateChatHistory(message.message, 'user'); 
        }
    };

    const addMessageItem = (item) => {
        setMessageItems(prev => [...prev, item]);
      };
    
    // Microphone setup
    useEffect(() => {
        const setupMicrophone = async () => {
            try {
                if (usingAgent) {
                    // Agent approach microphone handling
                    if (isMicOn && status === "connected") {
                        await navigator.mediaDevices.getUserMedia({ audio: true });
                    } else {
                        setCandSpeaking(false);
                    }
                } else if (usingRealtime) {
                    if (usingRealtime && localStreamRef.current) {
                        localStreamRef.current.getAudioTracks().forEach((track) => {
                          track.enabled = isMicOn;
                        });
                        console.log(`[Realtime] 🎙️ Microphone ${isMicOn ? "unmuted" : "muted"}`);
                      }
                    if (isMicOn) {
                        setMicrophoneOn(true);
                        setCandSpeaking(true);
                    } else {
                        setMicrophoneOn(false);
                        setCandSpeaking(false);
                    }
                } else {
                    // Traditional approach microphone handling
                    const canOpenMicrophone = isMicOn && isSocketOn && !isUttering;
                
                    if (!isMicrophoneOn && canOpenMicrophone) {
                        if(dg_mic && dg_mic.state !== 'recording'){
                            openMicrophone();
                        }
                        setMicrophoneOn(true);
                        setCandSpeaking(true);
                    } else if (isMicrophoneOn && (!isSocketOn || isUttering)) {
                        closeMicrophone();
                        setMicrophoneOn(false);
                        setCandSpeaking(false);
                    } else if (!isMicrophoneOn && dg_mic && dg_mic.state === 'recording') {
                        closeMicrophone();
                        setMicrophoneOn(false);
                        setCandSpeaking(false);
                    }
                }
            } catch (error) {
                console.error("Microphone setup error:", error);
                setCandSpeaking(false);
            }
        };
        setupMicrophone();
    }, [isMicOn, status, isSocketOn, isUttering, usingAgent, isMicrophoneOn, usingRealtime]);

    // Check browser capabilities and initialize microphone for non-agent approach
    useEffect(() => {
        // Initialize microphone for non-agent approach
        if (!usingAgent && !usingRealtime) {
          const getMic = async () => {
            dg_mic = await getMicrophone();
          }
          getMic();
        }
    }, [usingAgent, usingRealtime]);

    // Monitor AI speaking status
    useEffect(() => {
        if (usingAgent) {
            setIntvSpeaking(isSpeaking);
            setCandSpeaking(!isSpeaking);
        }
    }, [isSpeaking, usingAgent]);

    // Update reference to microphone state
    useEffect(() => {
        isMicrophoneOnRef.current = isMicrophoneOn;
    }, [isMicrophoneOn]);

    // Initialize parameters from URL
    useEffect(() => {
        const queryParams = window.location.search.slice(1);
        const paramsArray = queryParams.split('&');
        const jobParam = paramsArray.find(param => param.startsWith('job='));

        if (jobParam) {
            const jobIdValue = jobParam.split('=')[1];
            setJobId(jobIdValue);
        } else if (quickMockJobId) {
            setQMJobId(quickMockJobId);
        } else {
            setDefaultInterview(true);
        }
    }, []);

    // Start interview when parameters are ready
    useEffect(() => {
        if (jobId !== null || QMJobId !== null || defaultInterview) {
            startInterview();
        }
    }, [jobId, QMJobId, defaultInterview]);

    // Update interview prompt when promptId is available
    useEffect(() => {
        setInterviewPrompt(promptId, interviewQuestion); 
    }, [promptId, interviewQuestion]);

    // Handle non-agent audio streaming
    useEffect(() => {
        if(isUttering && !usingAgent && !usingRealtime){
            const mediaSource = new MediaSource();
            let sourceBuffer;
            let audioChunks = [];
            let allDataReceived = false;
            let fullText = '';

            let audio = new Audio();
            let objectURL = URL.createObjectURL(mediaSource);
            audio.src = objectURL;

            mediaSource.addEventListener('sourceopen', () =>{
                if(!sourceBuffer){
                    sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg');
                    sourceBuffer.addEventListener('updateend', () => {
                        if (audioChunks.length > 0 && !sourceBuffer.updating) {
                            sourceBuffer.appendBuffer(audioChunks.shift());
                        }
                    });
                }
            });

            if (wsRef.current) {
                wsRef.current.onmessage = (event) => {
                    if (event.data instanceof Blob) {
                        const reader = new FileReader();
                        reader.onload = () => {
                            audioChunks.push(reader.result);
                            if (sourceBuffer && !sourceBuffer.updating) {
                                sourceBuffer.appendBuffer(audioChunks.shift());
                            }
                        };
                        reader.readAsArrayBuffer(event.data);
                    } else if (typeof event.data === 'string') {
                        if (event.data === "sentence finished") {
                            if (audioChunks.length > 0 && !sourceBuffer.updating) {
                                sourceBuffer.appendBuffer(audioChunks.shift());
                            }
                        } else {
                            allDataReceived = true;
                            fullText = event.data;
                            if (!sourceBuffer.updating && audioChunks.length === 0) {
                                mediaSource.endOfStream();
                            }
                        }
                    }
                };
            }

            audio.addEventListener('play', () => {
                setIntvSpeaking(true);
                console.log("[INFO] Interviewer speaking");
                clearTimeout(waitingTimeout);
            });

            audio.addEventListener('ended', () =>{
                console.log("[INFO] Interviewer got all the data for speaking");
            });

            audio.addEventListener('waiting', () => {
                clearTimeout(waitingTimeout);
                waitingTimeout = setTimeout(() => {
                    audio.dispatchEvent(new Event('ended'));
                }, aiTimeOutSeconds);
            });

            audio.onended = () => {
                if (allDataReceived && mediaSource.readyState === 'open') {
                    mediaSource.endOfStream();
                }
                if(allDataReceived){
                    setIntvSpeaking(false); // turn of indicator
                    utterOnEnd(fullText);
                }
            };

            audio.play().catch(error => console.error("Error playing audio:", error));

            return () => {
                if (wsRef.current) {
                    wsRef.current.onmessage = null;
                }
                if (mediaSource.readyState === 'open') {
                    mediaSource.endOfStream();
                }
                audio.pause();
                audio.src = '';
                URL.revokeObjectURL(objectURL);
            };   
        } 
    }, [isUttering, usingAgent, usingRealtime]);

    // Start interview - main function
    const startInterview = async () => {
        if (isPracticeMode && practiceQuestion) {
            try {
                console.log("[INFO] Practice mode initialized:", {
                    company: practiceQuestion.company,
                    difficulty: practiceQuestion.difficulty,
                    questionType: practiceQuestion.standardizedQuestionType
                });
                
                
                const fetchPromptUrl = process.env.REACT_APP_BACKEND_URL + "/api/userData/interviewPrompt";
                const dataToSend = {
                    round: 'practice',
                    practice: true,
                    // Practice-specific data
                    specificQuestion: practiceQuestion.interviewQuestion,
                    companyName: practiceQuestion.company,
                    jobTitle: practiceQuestion.jobTitle,
                    questionType: practiceQuestion.standardizedQuestionType,
                    interviewRound: practiceQuestion.interviewType,
                    difficulty: practiceQuestion.difficulty,
                    practiceQuestionId: practiceQuestion._id,
                    // Optional: additional context
                    experienceLevel: practiceQuestion.experienceLevel,
                    tags: practiceQuestion.tags,
                    comments: practiceQuestion.comments
                };
                
                const response = await axios.post(fetchPromptUrl, dataToSend);
                initialQuestion = response.data.initialQuestion;
                systemPrompt = response.data.systemPrompt;
                promptId = response.data.promptId;
                interviewQuestion = response.data.interviewQuestion;
                console.log("[INFO] response.data is:", response.data);
                console.log("[INFO] Practice mode initialized:", {
                    company: practiceQuestion.company,
                    difficulty: practiceQuestion.difficulty,
                    questionType: practiceQuestion.standardizedQuestionType
                });
                
                promptJson = {
                    conversation: [{
                        role: 'system',
                        content: systemPrompt
                    }, {
                        role: 'assistant',
                        content: initialQuestion
                    }]
                };
                
                if (usingAgent) {
                    console.log("[INFO] Initializing agent session");
                    await conversation.startSession({
                        agentId: agentId,
                    });
                } else if (usingRealtime) {
                    console.log("[INFO] Initializing realtime session");
                    await initializeRealtimeSession();
                } else {
                    console.log("[INFO] Initializing traditional session");
                    fetchChatCompletion(true, false);
                    setInitialQuestionFinished(true);
                }
                
            } catch (error) {
                console.error('Error starting practice interview:', error);
                alert('Failed to start practice interview. Please try again.');
            }
        } else if(isB2BInterview){
            try {
                const res = await axios.get(`${process.env.REACT_APP_B2B_BACKEND_URL}/applications/${jobListingId}/interview-details`);
                const jobData = res.data;

                const fetchPromptUrl = process.env.REACT_APP_BACKEND_URL + "/api/userData/interviewPrompt";
                const dataToSend = {
                    round: "hr-round",
                };
                
                if (jobId) {
                    dataToSend.jobId = jobId;
                }
                if (QMJobId) {
                    dataToSend.quickMockJobId = QMJobId;
                }   
                const response = await axios.post(fetchPromptUrl, dataToSend);
                initialQuestion = response.data.initialQuestion;
                systemPrompt = response.data.systemPrompt;
                promptId = response.data.promptId;
                
                if (round === 'hr-round') {
                    systemPrompt = `${systemPrompt}\n\`\`\`\n${resume}\n\`\`\``;
                    if (jd) {
                        systemPrompt += `\n\n Here's the job description \n\`\`\`\n${jd}\n\`\`\``;
                    }
                }
                
                if (['prod-sense', 'analytical-execution', 'leadership'].includes(round)) {
                    interviewQuestion = response.data.interviewQuestion;
                }
                console.log("[INFO] Initial question is:", initialQuestion);
                promptJson = {
                    conversation: [{
                        role: 'system',
                        content: systemPrompt
                    }, {
                        role: 'assistant',
                        content: initialQuestion
                    }]
                };
                
                if (usingAgent) {
                    // Use ElevenLabs agent approach
                    console.log("[11labs] Starting session with agentId:", agentId);
                    await conversation.startSession({
                        agentId: agentId,
                    });
                } else if (usingRealtime) {
                    // Use OpenAI Realtime API approach
                    await initializeRealtimeSession();
                } else {
                    // Use traditional approach
                    if(!wsRef.current) {
                        uuid = uuidv4();
                        const wsUrl = new URL(process.env.REACT_APP_WS_ADDRESS);
                        wsUrl.searchParams.append('session-id', uuid);
                        wsRef.current = new WebSocket(wsUrl.toString());
                    }
                    
                    setIsUttering(true);
                    
                    interviewStartTime = new Date().getTime();
                    setTimeout(() => {
                        fetchChatCompletion(true, false);
                    }, timeOutSecondsAfterJoining);
                }
            } catch (error) {
                console.error("Error starting interview:", error);
            }
        }
        else{
            try {
                const fetchPromptUrl = process.env.REACT_APP_BACKEND_URL + "/api/userData/interviewPrompt";
                const dataToSend = {
                    round: round,
                };
                
                if (jobId) {
                    dataToSend.jobId = jobId;
                }
                if (QMJobId) {
                    dataToSend.quickMockJobId = QMJobId;
                }
                const response = await axios.post(fetchPromptUrl, dataToSend);
                initialQuestion = response.data.initialQuestion;
                systemPrompt = response.data.systemPrompt;
                promptId = response.data.promptId;
                
                if (round === 'hr-round') {
                    systemPrompt = `${systemPrompt}\n\`\`\`\n${resume}\n\`\`\``;
                    if (jd) {
                        systemPrompt += `\n\n Here's the job description \n\`\`\`\n${jd}\n\`\`\``;
                    }
                }
                
                if (['prod-sense', 'analytical-execution', 'leadership'].includes(round)) {
                    interviewQuestion = response.data.interviewQuestion;
                }
                console.log("[INFO] Initial question is:", initialQuestion);
                promptJson = {
                    conversation: [{
                        role: 'system',
                        content: systemPrompt
                    }, {
                        role: 'assistant',
                        content: initialQuestion
                    }]
                };
                if (usingAgent) {
                    // Use ElevenLabs agent approach
                    console.log("[11labs] Starting session with agentId:", agentId);
                    await conversation.startSession({
                        agentId: agentId,
                    });
                } else if (usingRealtime) {
                    // Use OpenAI Realtime API approach
                    await initializeRealtimeSession();
                } else {
                    // Use traditional approach
                    if(!wsRef.current) {
                        uuid = uuidv4();
                        const wsUrl = new URL(process.env.REACT_APP_WS_ADDRESS);
                        wsUrl.searchParams.append('session-id', uuid);
                        wsRef.current = new WebSocket(wsUrl.toString());
                    }
                    
                    setIsUttering(true);
                    
                    interviewStartTime = new Date().getTime();
                    setTimeout(() => {
                        fetchChatCompletion(true, false);
                    }, timeOutSecondsAfterJoining);
                }
            } catch (error) {
                console.error("Error starting interview:", error);
            }
        }
    };

    // Update chat history for ElevenLabs agent approach
    const updateChatHistory = (text, role) => {
        setChatHistory(prev => {
            const newHistory = [...prev, { role, text }];
            globalChatHistory = newHistory; 
            return newHistory;
        });
    };

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({
                behavior: "smooth",
                block: "end"
            });
        }
    }, [chatHistory, dgFinalTranscriptHist, realtimeTranscriptions, realtimeResponses]);

    // Traditional approach functions
    const fetchChatCompletion = (isInitialQuestion, textGen) => {
        const dataToSend = {
            conversation: promptJson.conversation,
            isInitialQuestionTag: isInitialQuestion,
            textGen: textGen,
            clientuuid: uuid,
            language: language,
        };
        const stringfyData = JSON.stringify(dataToSend, null, 2);
        const headers = {
            'Content-Type': 'application/json',
        };
        const url = process.env.REACT_APP_BACKEND_URL + '/api/openai/chatCompletion';
        console.log('[INFO] Fetching chat completion');
        axios.post(url, stringfyData, { headers })
            .then(response => {
                console.log("[INFO] Fetch chat completion finishes");
            })
            .catch(error => {
                console.error(error);
            });
    };

    const utterOnEnd = async (fullText) => {
        if(hasInitialQuestionFinished) {
            chatCompletionHist.push(fullText);
            if (!isPostingNotice) {
                updatePromptRef.current('assistant', fullText);
            } else {
                isPostingNotice = false;
            }
        } else {
            setInitialQuestionFinished(true);
        }

        setIsUttering(false);
        await turnOnSocket();
    };

    // Deepgram functions
    const getTempApiKey = async() => {
        const result = await fetch(process.env.REACT_APP_BACKEND_URL + "/api/dgKey");
        const json = await result.json();
        return json.key;
    };

    const getMicrophone = async() => {
        const userMedia = await navigator.mediaDevices.getUserMedia({
            audio: true,
        });
        return new MediaRecorder(userMedia);
    };

    const openMicrophone = async() => {
        await dg_mic.start(0);
        dg_mic.ondataavailable = (e) => {
            if (isMicrophoneOnRef.current) {
                const data = e.data;
                dg_socket.send(data);
            }
        };
    };

    const closeMicrophone = async() => {
        if(dg_mic) dg_mic.stop();
    };

    const turnOnSocket = async () => {
        const key = await getTempApiKey();
        const dg_client = createClient(key);
        
        dg_socket = dg_client.listen.live({ 
            model: "nova-2",
            smart_format: true,
            interim_results: true,
            utterance_end_ms: timeOutSeconds,
            language: language,
        });
        
        let userSpeakingOnEndCalled = false;

        dg_socket.on(LiveTranscriptionEvents.Open, () => {
            console.log("[DG] connected to websocket");
            let concatenated = "";
            var transcript = "";
            setSocketOn(true);
            
            const dg_finish_interval = setInterval(() => {
                closeMicrophone();
                
                if (remainingRetryTimes == 0) {
                    showReconnectionPopUp();
                    remainingRetryTimes = sessionAliveRetryTimes;
                } else {
                    askUserToRepeat();
                }
                clearInterval(dg_finish_interval);
            }, sessionTimeOutSeconds);
            
            dg_socket.on(LiveTranscriptionEvents.Transcript, (data) => {
                transcript = data.channel.alternatives[0].transcript;

                if (transcript !== "") {
                    clearInterval(dg_finish_interval);
                    remainingRetryTimes = sessionAliveRetryTimes;

                    if (!data.is_final) {
                        setCandSpeaking(true);
                        console.log("[DG] interim:", transcript);
                        setDgTempTranscript(transcript);
                    } else {
                        concatenated = concatenated + " " + transcript;
                        console.log("[DG] final:", concatenated);
                        setDgFinalTranscript(concatenated);
                        setDgTempTranscript("");
                    }
                }
            });

            dg_socket.on(LiveTranscriptionEvents.UtteranceEnd, (data) => {
                console.log("[DG] full:", concatenated);
                if (!userSpeakingOnEndCalled) {
                    userSpeakingOnEndCalled = true;
                    userSpeakingOnEnd(concatenated);
                }
                concatenated = "";
                setCandSpeaking(false);
            });

            dg_socket.on(LiveTranscriptionEvents.Error, (e) => console.error(e));

            dg_socket.on(LiveTranscriptionEvents.Close, (e) => {
                if (!userSpeakingOnEndCalled && (concatenated || transcript)) {
                    userSpeakingOnEndCalled = true;
                    userSpeakingOnEnd(concatenated || transcript);
                    concatenated = "";
                    transcript = "";
                }
                setSocketOn(false);
                setCandSpeaking(false);
            });
        });
    };

    const askUserToRepeat = () => {
        updatePromptRef.current('user', "");
        dgFinalTranscriptHist.push("");
        updatePromptRef.current('assistant', sessionExpireNotice);
        fetchChatCompletion(false, false);
        isPostingNotice = true;
        setIsUttering(true);
        remainingRetryTimes -= 1;
    };
    
    const showReconnectionPopUp = () => {
        Swal.fire({
            title: `<div class="normal-text-mini">${sessionResumeText}</div>`,
            confirmButtonText: "Resume",
            confirmButtonColor: "var(--color-primary-light)",
            reverseButtons: true,
            allowOutsideClick: false,
        }).then((result) => {
            if (result.isConfirmed) {
                if (usingAgent) {
                  console.log("[INFO] Resuming agent session");
                  conversation.startSession({ agentId: agentId });
                } else if (usingRealtime) {
                  console.log("[INFO] Resuming realtime session");
                  initializeRealtimeSession();
                } else {
                  turnOnSocket(); // Restart traditional socket
                }
            }
        });
    };

    const userSpeakingOnEnd = (concatenated) => {
        dgFinalTranscriptHist.push(concatenated);
        handleNewUserInput(concatenated);
    };

    const handleNewUserInput = (userInput) => {
        updatePromptRef.current('user', userInput);
        fetchChatCompletion(false, true);
        setIsUttering(true);
        dg_socket.finish();
        setDgFinalTranscript("");
        setDgTempTranscript("");
    };
    const addRealtimeResponse = (newText) => {
        setRealtimeResponses(prev => [...prev, newText]);
    };

    const addRealtimeTranscription = ({ text }) => {
        setRealtimeTranscriptions(prev => [...prev, text]);
    };

    const initializeRealtimeSession = async () => {
        const { peerConnection, dataChannel, localStream } = await connectRealtime({
          systemPrompt,
          audioElementRef,
          isAudioPlaybackEnabled: true,
          isMicOn,
          onServerEvent: (event) =>
            handleServerEvent(event, {
              dataChannel,
              setIntvSpeaking,
              setCandSpeaking,
              updatePromptJson: updatePromptRef.current,
              setMessageItems,
            }),
        });
      
        if (peerConnection && dataChannel && localStream) {
          peerConnectionRef.current = peerConnection;
          realtimeDataChannelRef.current = dataChannel;
          localStreamRef.current = localStream;
          isRealtimeConnectedRef.current = true;
        } else {
          console.error("[Realtime] ❌ Failed to initialize realtime session");
        }
      };
      
      
    
    // Clean up resources when component unmounts
    useEffect(() => {
        return () => {
            if (usingRealtime) {
                cleanupRealtimeConnection();
            } else if (!usingAgent) {
                closeMicrophone();
            }
        };
    }, [usingRealtime, usingAgent]);

    const cleanupRealtimeConnection = () => {
        if (peerConnectionRef.current) {
          peerConnectionRef.current.close();
          peerConnectionRef.current = null;
        }
      
        if (realtimeDataChannelRef.current) {
          realtimeDataChannelRef.current.close();
          realtimeDataChannelRef.current = null;
        }
      
        if (localStreamRef.current) {
          localStreamRef.current.getTracks().forEach(track => track.stop());
          localStreamRef.current = null;
        }
      
        isRealtimeConnectedRef.current = false;
      };
      

    return (
        <Box className="mainScreen-content">
            {isccOn && (
                <Box className="stt-chatHistory-container">
                    <Box className="stt-chatHistory-content">
                        {usingAgent ? (
                            // ElevenLabs agent UI
                            <>
                                {chatHistory.map((message, index) => (
                                    <div 
                                        key={index}
                                        className={`stt-flex-content ${message.role} ${
                                            index === chatHistory.length - 1 ? 'latest-message' : ''
                                        }`}
                                    >
                                        {message.role === 'assistant' ? (
                                            <>
                                                <Avatar className="stt-intv-avatar">INTV</Avatar>
                                                <p className="normal-text stt-p stt-interviewer-content">
                                                    {message.text}
                                                </p>
                                            </>
                                        ) : (
                                            <>
                                                <p className="normal-text stt-p stt-candidate-content">
                                                    {message.text}
                                                </p>
                                                <Avatar className="stt-cand-avatar">CAND</Avatar>
                                            </>
                                        )}
                                    </div>
                                ))}
                                <div ref={messagesEndRef} />
                            </>
                        ) : usingRealtime ? (
                            <>
                              {messageItems.map((msg, i) => (
                                <div key={i} className={`stt-flex-content ${msg.role}`}>
                                    {msg.role === "assistant" && (
                                    <>
                                        <Avatar className="stt-intv-avatar">INTV</Avatar>
                                        <p
                                        className={`stt-p stt-interviewer-content ${
                                            msg.streaming ? "fading-message" : ""
                                        }`}
                                        >
                                        {msg.text}
                                        </p>
                                    </>
                                    )}
                                    {msg.role === "user" && (
                                    <>
                                        <p className="stt-p stt-candidate-content">{msg.text}</p>
                                        <Avatar className="stt-cand-avatar">CAND</Avatar>
                                    </>
                                    )}
                                </div>
                                ))}
                              <div ref={messagesEndRef} />
                            </>
                          ) : (
                            // Traditional approach UI
                            <>
                                <div className="stt-flex-content assistant">
                                    <Avatar className="stt-intv-avatar">INTV</Avatar>
                                    <p className="normal-text stt-p stt-interviewer-content">
                                        {hasInitialQuestionFinished ? initialQuestion : "Initializing questions..."}
                                    </p>
                                </div>
    
                                {dgFinalTranscriptHist.map((item, index) => (
                                    <div key={index}>
                                        {item && (
                                            <div className="stt-flex-content user">
                                                <p className="normal-text stt-p stt-candidate-content">
                                                    {item}
                                                </p>
                                                <Avatar className="stt-cand-avatar">CAND</Avatar>
                                            </div>
                                        )}
                                        <div className="stt-flex-content assistant">
                                            <Avatar className="stt-intv-avatar">INTV</Avatar>
                                            <p className="normal-text stt-p stt-interviewer-content">
                                                {chatCompletionHist[index] || "Generating response..."}
                                            </p>
                                        </div>
                                    </div>
                                ))}
    
                                {(dgTempTranscript || dgFinalTranscript) && (
                                    <div className="stt-flex-content user fading-message">
                                        <p className="normal-text stt-p stt-candidate-content">
                                            {dgFinalTranscript}
                                            {dgTempTranscript && (
                                                <span className="temp-transcript">{dgTempTranscript}</span>
                                            )}
                                        </p>
                                        <Avatar className="stt-cand-avatar">CAND</Avatar>
                                    </div>
                                )}
                                
                                <div ref={messagesEndRef} />
                            </>
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

// Export chat history function
export function getChatHist() {
    if (globalChatHistory.length > 0) {
        return globalChatHistory;
    } else if (promptJson && promptJson.conversation) {
        return promptJson.conversation.slice(1); // to exclude the system prompt
    }
    return [];
}

export default SpeechToText;
